
import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SignInForm } from '@/components/auth/SignInForm';
import { SignUpForm } from '@/components/auth/SignUpForm';
import { ForgotPasswordForm } from '@/components/auth/ForgotPasswordForm';
import { PageWrapper } from '@/components/layout/PageWrapper';
import { useToast } from '@/hooks/use-toast';

type AuthMode = 'signin' | 'signup' | 'forgot-password';

const Auth = () => {
  const [mode, setMode] = useState<AuthMode>('signin');
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const hasShownToast = useRef(false);

  // Détecter si l'utilisateur vient de confirmer son email
  useEffect(() => {
    if (searchParams.get('confirmed') === 'true' && !hasShownToast.current) {
      hasShownToast.current = true;
      
      toast({
        title: "Email confirmé !",
        description: "Votre compte est maintenant actif. Vous pouvez vous connecter.",
        duration: 6000
      });
      
      // Nettoyer l'URL pour éviter d'afficher le message à chaque refresh
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, toast]);

  return (
    <PageWrapper layout="no-sidebar">
      <div className="min-h-screen flex w-full auth-page">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center px-8 py-12 bg-white">
        <div className="w-full max-w-md">
          {mode === 'signin' && (
            <SignInForm
              onSwitchToSignUp={() => setMode('signup')}
              onForgotPassword={() => setMode('forgot-password')}
            />
          )}
          {mode === 'signup' && (
            <SignUpForm
              onSwitchToSignIn={() => setMode('signin')}
            />
          )}
          {mode === 'forgot-password' && (
            <ForgotPasswordForm
              onBackToSignIn={() => setMode('signin')}
            />
          )}
        </div>
      </div>

      {/* Right side - Illustration */}
      <div className="hidden lg:flex flex-1 relative p-6">
        {/* Turquoise border */}
        <div className="w-full h-full bg-gradient-to-br from-teal-400 to-teal-500 p-4 rounded-[2rem]">
          {/* Dark background with pattern */}
          <div className="w-full h-full rounded-2xl relative overflow-hidden" style={{ backgroundColor: '#172D2B' }}>
            {/* Checkmark pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-8 left-8 text-teal-400 text-2xl">✓</div>
              <div className="absolute top-16 right-12 text-teal-400 text-xl">✓</div>
              <div className="absolute top-32 left-16 text-teal-400 text-lg">✓</div>
              <div className="absolute top-48 right-8 text-teal-400 text-2xl">✓</div>
              <div className="absolute bottom-32 left-8 text-teal-400 text-xl">✓</div>
              <div className="absolute bottom-16 right-16 text-teal-400 text-lg">✓</div>
              <div className="absolute bottom-48 left-32 text-teal-400 text-2xl">✓</div>
              <div className="absolute top-64 right-32 text-teal-400 text-xl">✓</div>
            </div>

            {/* Content */}
            <div className="relative z-10 flex flex-col items-center justify-center h-full p-12 text-center">
              {/* Real Dashboard screenshots */}
              <div className="mb-16 relative">
                <div className="rounded-lg shadow-xl p-0.5 mb-4 transform rotate-3 w-[26rem] h-64" style={{ backgroundColor: '#40E0D0' }}>
                  <img
                    src="https://pvmemyboxgefsaordzqj.supabase.co/storage/v1/object/public/App%20image/Rapport%20sample.svg"
                    alt="Dashboard Rapport Getproof - Évaluation candidats"
                    className="w-full h-full object-cover rounded"
                  />
                </div>
                <div className="rounded-lg shadow-xl p-0.5 transform -rotate-2 w-[22rem] h-52 absolute -bottom-4 -right-4" style={{ backgroundColor: '#40E0D0' }}>
                  <img
                    src="https://pvmemyboxgefsaordzqj.supabase.co/storage/v1/object/public/App%20image/Stats%20screen.png"
                    alt="Dashboard Statistiques Getproof - KPIs et performances"
                    className="w-full h-full object-cover rounded"
                  />
                </div>
              </div>

              {/* Text content */}
              <div className="max-w-md">
                <h2 className="text-lg font-bold mb-4" style={{ color: '#7AD5CA' }}>
                  Avec Getproof, automatisez vos prises de référence et gagnez en efficacité dans vos recrutements.
                </h2>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </PageWrapper>
  );
};

export default Auth;
